import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PurchasesNav from '../components/PurchasesNav';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
  Autocomplete,
  IconButton,
  Chip,
  Alert,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PublicIcon from '@mui/icons-material/Public';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import {
  purchaseOrderService,
  purchaseRequestService,
  rawMaterialService,
  dyeService,
  packagingMaterialService,
  supplierDirectoryService,
  consumableMaterialService,
  pressFormService,
} from '../services/api';

const STATUS_OPTIONS = [
  { value: 'new', label: 'Нова' },
  { value: 'in_progress', label: 'В роботі' },
  { value: 'ordered', label: 'Замовлено' },
  { value: 'completed', label: 'Виконано' },
];

const MATERIAL_TAB = ['raw', 'dye', 'packaging', 'consumable', 'press_form', 'equipment'];
const REQUESTS_TAB_INDEX = 6;
const ZED_TAB_INDEX = 7;

function purchaseToMaterialTabIndex(p) {
  if (!p) return 0;
  if ((p.equipment_name || '').trim()) return 5;
  if (p.press_form) return 4;
  if (p.consumable_material) return 3;
  if (p.packaging_material) return 2;
  if (p.dye) return 1;
  if (p.raw_material) return 0;
  return 0;
}

let payKeySeq = 0;
const nextPayKey = () => {
  payKeySeq += 1;
  return `pay${payKeySeq}`;
};

let shipKeySeq = 0;
const nextShipKey = () => {
  shipKeySeq += 1;
  return `ship${shipKeySeq}`;
};

let itemKeySeq = 0;
const nextItemKey = () => {
  itemKeySeq += 1;
  return `item${itemKeySeq}`;
};

const EMPTY_ITEM = () => ({
  tempKey: nextItemKey(),
  materialType: 'raw',
  raw_material: '',
  dye: '',
  packaging_material: '',
  consumable_material: '',
  press_form: '',
  equipment_name: '',
  quantity: '',
  unit_price: '',
});

function toDateTimeLocalFromIso(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function paymentPaidAtToIso(localVal) {
  if (!localVal) return null;
  const d = new Date(localVal);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function notifyPurchaseApprovalsChanged() {
  window.dispatchEvent(new CustomEvent('purchase-approvals-updated'));
}

function PurchasesPage({ currentUser }) {
  const [purchases, setPurchases] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [dyes, setDyes] = useState([]);
  const [packaging, setPackaging] = useState([]);
  const [consumables, setConsumables] = useState([]);
  const [pressForms, setPressForms] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formError, setFormError] = useState('');
  const [editing, setEditing] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [purchaseRequests, setPurchaseRequests] = useState([]);
  const [purchaseRequestsLoading, setPurchaseRequestsLoading] = useState(false);
  const [pendingFulfillLineId, setPendingFulfillLineId] = useState(null);
  const [pendingPurchaseRequestId, setPendingPurchaseRequestId] = useState(null);
  const [paymentRows, setPaymentRows] = useState([]);
  const [shipmentRows, setShipmentRows] = useState([]);
  const [itemRows, setItemRows] = useState([EMPTY_ITEM()]);
  const [reapprovalSubmitting, setReapprovalSubmitting] = useState(false);

  // ЗЕД state (embedded in purchase dialog)
  const [zedEnabled, setZedEnabled] = useState(false);
  const [zedDetailId, setZedDetailId] = useState(null); // existing ForeignPurchaseDetail id
  const [zedForm, setZedForm] = useState({
    currency: 'USD',
    exchange_rate: '',
    amount_foreign: '',
    payment_type: 'postpayment',
    payment_date: '',
    expected_delivery_date: '',
    actual_delivery_date: '',
    logistics_delivery: '',
    logistics_customs: '',
    logistics_insurance: '',
    customs_declaration_number: '',
    currency_control_closed: false,
    note: '',
  });

  const [form, setForm] = useState({
    purchase_date: '',
    delivery_date_planned: '',
    arrival_date_planned: '',
    arrival_date_actual: '',
    status: 'new',
    supplier: '',
    supplier_name: '',
    comment: '',
  });

  const getWarehouseTitleByMaterialType = (type) => {
    if (type === 'raw') return 'Склад сировини';
    if (type === 'dye') return 'Склад барвників';
    if (type === 'packaging') return 'Склад упаковки';
    if (type === 'consumable') return 'Склад розхідних';
    if (type === 'press_form') return 'Не на склад';
    if (type === 'equipment') return 'Не на склад';
    return '—';
  };

  const getWarehouseTitleForPurchase = (p) => {
    const items = Array.isArray(p.items) ? p.items : [];
    const hasPress = !!p.press_form || items.some((it) => it.press_form || it.line_kind === 'press_form');
    const hasEquip =
      !!(p.equipment_name || '').trim()
      || items.some((it) => it.line_kind === 'equipment' || (!!((it.equipment_name || '').trim()) && it.line_kind !== 'press_form' && !it.press_form));
    if (hasPress || hasEquip) return 'Не на склад';
    if (p.raw_material) {
      return p.raw_material_store_category === 'technical' ? 'Склад техматеріалів' : 'Склад сировини';
    }
    if (p.dye) return 'Склад барвників';
    if (p.packaging_material) return 'Склад упаковки';
    if (p.consumable_material) return 'Склад розхідних';
    return '—';
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pRes, rawRes, dyeRes, packRes, supRes, consumableRes, pfRes] = await Promise.all([
        purchaseOrderService.getAll(),
        rawMaterialService.getAll(),
        dyeService.getAll(),
        packagingMaterialService.getAll(),
        supplierDirectoryService.getAll({ is_active: true }),
        consumableMaterialService.getAll(),
        pressFormService.getAll(),
      ]);
      const normalize = (res) =>
        Array.isArray(res.data?.results) ? res.data.results : Array.isArray(res.data) ? res.data : [];
      setPurchases(normalize(pRes));
      setRawMaterials(normalize(rawRes));
      setDyes(normalize(dyeRes));
      setPackaging(normalize(packRes));
      setConsumables(normalize(consumableRes));
      setPressForms(normalize(pfRes));
      setSuppliers(normalize(supRes));
    } catch (error) {
      console.error(error);
      alert('Помилка завантаження закупівель');
    } finally {
      setLoading(false);
    }
  };

  const loadPurchaseRequests = async () => {
    try {
      setPurchaseRequestsLoading(true);
      const res = await purchaseRequestService.getAll();
      const normalize = (r) =>
        Array.isArray(r.data?.results) ? r.data.results : Array.isArray(r.data) ? r.data : [];
      setPurchaseRequests(normalize(res));
    } catch (e) {
      console.error(e);
      setPurchaseRequests([]);
    } finally {
      setPurchaseRequestsLoading(false);
    }
  };

  const [highlightPrId, setHighlightPrId] = useState(null);
  const [highlightPoId, setHighlightPoId] = useState(null);

  useEffect(() => {
    const view = searchParams.get('view');
    const prRaw = searchParams.get('highlight_pr');
    const poRaw = searchParams.get('highlight_po');

    if (poRaw != null && /^\d+$/.test(String(poRaw).trim())) {
      setHighlightPrId(null);
      setHighlightPoId(Number(poRaw));
      return;
    }
    setHighlightPoId(null);

    const openRequests =
      view === 'requests' || (prRaw != null && /^\d+$/.test(String(prRaw).trim()));
    if (openRequests) {
      setTabValue(REQUESTS_TAB_INDEX);
      loadPurchaseRequests();
    }
    if (prRaw != null && /^\d+$/.test(String(prRaw).trim())) {
      setHighlightPrId(Number(prRaw));
    } else {
      setHighlightPrId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadPurchaseRequests стабільний за змістом
  }, [searchParams]);

  useEffect(() => {
    if (highlightPoId == null || loading || tabValue >= MATERIAL_TAB.length) return;
    const p = purchases.find((x) => Number(x.id) === Number(highlightPoId));
    if (!p) return;
    const wantTab = purchaseToMaterialTabIndex(p);
    if (tabValue !== wantTab) {
      setTabValue(wantTab);
      return;
    }
    const t = window.requestAnimationFrame(() => {
      const el = document.getElementById(`po-row-${highlightPoId}`);
      if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(t);
  }, [highlightPoId, tabValue, loading, purchases]);

  useEffect(() => {
    if (highlightPrId == null || tabValue !== REQUESTS_TAB_INDEX || purchaseRequestsLoading) return;
    const t = window.requestAnimationFrame(() => {
      const el = document.getElementById(`pr-row-${highlightPrId}`);
      if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(t);
  }, [highlightPrId, tabValue, purchaseRequestsLoading, purchaseRequests.length]);

  const handleMainTabChange = (e, v) => {
    setTabValue(v);
    if (v === REQUESTS_TAB_INDEX) {
      setSearchParams({ view: 'requests' });
      loadPurchaseRequests();
    } else if (v === ZED_TAB_INDEX) {
      setSearchParams({ view: 'zed' });
    } else {
      setSearchParams({});
    }
  };

  const editingPurchase = useMemo(
    () => (editing ? purchases.find((x) => Number(x.id) === Number(editing)) : null),
    [purchases, editing],
  );

  const isAwaitingApproval = (p) => p && p.approval_status === 'pending';
  const isRejectedApproval = (p) => p && p.approval_status === 'rejected';
  const canRequestPurchaseReapproval =
    (currentUser?.effective_permissions || {})['menu.purchases'] !== false;

  const dialogReadOnly = Boolean(editing) && isAwaitingApproval(editingPurchase);

  const filteredPurchases = useMemo(() => {
    if (tabValue >= MATERIAL_TAB.length) return [];
    const type = MATERIAL_TAB[tabValue];
    return purchases.filter((p) => {
      // Check flat fields (legacy single-item orders)
      const flatMatch = (() => {
        if (type === 'raw') return !!p.raw_material;
        if (type === 'dye') return !!p.dye;
        if (type === 'packaging') return !!p.packaging_material;
        if (type === 'consumable') return !!p.consumable_material;
        if (type === 'press_form') return !!p.press_form;
        if (type === 'equipment') return !!(p.equipment_name || '').trim();
        return false;
      })();
      if (flatMatch) return true;
      // Check items (new multi-item orders)
      const items = Array.isArray(p.items) ? p.items : [];
      if (items.length > 0) {
        return items.some((it) => {
          if (type === 'raw') return !!it.raw_material;
          if (type === 'dye') return !!it.dye;
          if (type === 'packaging') return !!it.packaging_material;
          if (type === 'consumable') return !!it.consumable_material;
          if (type === 'press_form') {
            return !!it.press_form || it.line_kind === 'press_form';
          }
          if (type === 'equipment') {
            return (
              it.line_kind === 'equipment'
              || (!!((it.equipment_name || '').trim()) && it.line_kind !== 'press_form' && !it.press_form)
            );
          }
          return false;
        });
      }
      return true;
    });
  }, [purchases, tabValue]);

  const supplierOptions = useMemo(
    () =>
      (suppliers || []).map((s) => ({
        id: s.id,
        label: s.edrpou ? `${s.name} (ЄДРПОУ ${s.edrpou})` : s.name,
        name: s.name,
      })),
    [suppliers],
  );

  const itemsTotal = useMemo(() => {
    return itemRows.reduce((sum, it) => {
      const qty = parseFloat(it.quantity);
      const up = parseFloat(it.unit_price);
      if (Number.isFinite(qty) && Number.isFinite(up)) return sum + qty * up;
      return sum;
    }, 0);
  }, [itemRows]);

  const paymentsSumInForm = useMemo(() => {
    let s = 0;
    for (const r of paymentRows) {
      const a = parseFloat(r.amount);
      if (Number.isFinite(a) && a > 0) s += a;
    }
    return Math.round(s * 100) / 100;
  }, [paymentRows]);

  const shipmentsSumInForm = useMemo(() => {
    let s = 0;
    for (const r of shipmentRows) {
      const q = parseFloat(r.quantity);
      if (Number.isFinite(q) && q > 0) s += q;
    }
    return Math.round(s * 10000) / 10000;
  }, [shipmentRows]);

  const resolveSupplierForMaterial = (matType, matId) => {
    const id = Number(matId);
    if (!Number.isFinite(id) || id <= 0) return { supplier: '', supplier_name: '' };

    const linked = (suppliers || []).find((s) => {
      if (matType === 'raw') return (s.raw_materials || []).map(Number).includes(id);
      if (matType === 'dye') return (s.dyes || []).map(Number).includes(id);
      return false;
    });
    if (linked) return { supplier: linked.id, supplier_name: linked.name || '' };

    const mat =
      matType === 'raw'
        ? (rawMaterials || []).find((m) => Number(m.id) === id)
        : matType === 'dye'
          ? (dyes || []).find((m) => Number(m.id) === id)
          : null;
    const hint = (mat?.supplier_name || '').trim();
    if (hint) {
      const byName = (suppliers || []).find(
        (s) => String(s.name || '').trim().toLowerCase() === hint.toLowerCase(),
      );
      if (byName) return { supplier: byName.id, supplier_name: byName.name || hint };
      return { supplier: '', supplier_name: hint };
    }

    // Остання закупівля з цим матеріалом
    for (const p of purchases || []) {
      const items = Array.isArray(p.items) && p.items.length ? p.items : [p];
      const hit = items.some((it) => {
        if (matType === 'raw') return Number(it.raw_material) === id;
        if (matType === 'dye') return Number(it.dye) === id;
        return false;
      });
      if (!hit) continue;
      if (p.supplier) return { supplier: p.supplier, supplier_name: p.supplier_name || '' };
      if ((p.supplier_name || '').trim()) return { supplier: '', supplier_name: p.supplier_name.trim() };
    }
    return { supplier: '', supplier_name: '' };
  };

  const openPurchaseFromRequestLine = (req, line) => {
    if (line.fulfilled_by_purchase) {
      alert('Для цього рядка вже створена закупівля.');
      return;
    }
    setPendingFulfillLineId(line.id);
    setPendingPurchaseRequestId(req.id);

    let matType = 'raw';
    let rawId = line.raw_material != null && line.raw_material !== '' ? String(line.raw_material) : '';
    let dyeId = line.dye != null && line.dye !== '' ? String(line.dye) : '';

    if (rawId) matType = 'raw';
    else if (dyeId) matType = 'dye';
    else {
      // Fallback: знайти в довіднику за назвою з заявки
      const name = String(line.material_name || '').trim().toLowerCase();
      if (name) {
        const rm = (rawMaterials || []).find((m) => String(m.name || '').trim().toLowerCase() === name);
        const dy = (dyes || []).find((m) => String(m.name || '').trim().toLowerCase() === name);
        if (rm) {
          matType = 'raw';
          rawId = String(rm.id);
        } else if (dy) {
          matType = 'dye';
          dyeId = String(dy.id);
        }
      }
    }

    const qty =
      line.quantity_needed != null && line.quantity_needed !== ''
        ? String(line.quantity_needed)
        : '';
    const { supplier, supplier_name } = resolveSupplierForMaterial(
      matType,
      matType === 'raw' ? rawId : dyeId,
    );

    setEditing(null);
    setZedEnabled(false);
    setZedDetailId(null);
    setForm({
      purchase_date: new Date().toISOString().slice(0, 10),
      delivery_date_planned: '',
      arrival_date_planned: '',
      arrival_date_actual: '',
      status: 'new',
      supplier: supplier || '',
      supplier_name: supplier_name || '',
      comment: `Заявка #${req.id}: ${(req.title || '').trim() || req.customer_order_number || ''}`.slice(0, 500),
    });
    setItemRows([{
      ...EMPTY_ITEM(),
      materialType: matType,
      raw_material: matType === 'raw' ? rawId : '',
      dye: matType === 'dye' ? dyeId : '',
      packaging_material: '',
      consumable_material: '',
      press_form: '',
      equipment_name: '',
      quantity: qty,
      unit_price: '',
    }]);
    setPaymentRows([]);
    setShipmentRows([]);
    setDialogOpen(true);
  };

  const openDialog = (purchase = null) => {
    setFormError('');
    if (purchase) {
      setPendingFulfillLineId(null);
      setPendingPurchaseRequestId(null);
      setEditing(purchase.id);
      setForm({
        purchase_date: purchase.purchase_date?.slice(0, 10) || '',
        delivery_date_planned: purchase.delivery_date_planned
          ? purchase.delivery_date_planned.slice(0, 10)
          : '',
        arrival_date_planned: purchase.arrival_date_planned
          ? purchase.arrival_date_planned.slice(0, 10)
          : '',
        arrival_date_actual: purchase.arrival_date_actual?.slice(0, 10) || '',
        status: purchase.status || 'new',
        supplier: purchase.supplier || '',
        supplier_name: purchase.supplier_name || '',
        comment: purchase.comment || '',
      });

      // Build itemRows from items (new multi-item) or fall back to single-item fields
      const existingItems = Array.isArray(purchase.items) && purchase.items.length > 0
        ? purchase.items
        : null;
      if (existingItems) {
        setItemRows(existingItems.map((item) => {
          let materialType = 'raw';
          if (item.dye) materialType = 'dye';
          if (item.packaging_material) materialType = 'packaging';
          if (item.consumable_material) materialType = 'consumable';
          if (item.press_form || item.line_kind === 'press_form') materialType = 'press_form';
          else if (item.line_kind === 'equipment' || (item.equipment_name || '').trim()) materialType = 'equipment';
          return {
            tempKey: item.id != null ? `saved-${item.id}` : nextItemKey(),
            id: item.id,
            materialType,
            raw_material: item.raw_material != null && item.raw_material !== '' ? String(item.raw_material) : '',
            dye: item.dye != null && item.dye !== '' ? String(item.dye) : '',
            packaging_material: item.packaging_material != null && item.packaging_material !== '' ? String(item.packaging_material) : '',
            consumable_material: item.consumable_material != null && item.consumable_material !== '' ? String(item.consumable_material) : '',
            press_form: item.press_form != null && item.press_form !== '' ? String(item.press_form) : '',
            equipment_name: (item.equipment_name || '').trim(),
            quantity: item.quantity != null ? String(item.quantity) : '',
            unit_price: item.unit_price != null ? String(item.unit_price) : '',
          };
        }));
      } else {
        // Legacy: single-item from PurchaseOrder flat fields
        let materialType = 'raw';
        if (purchase.dye) materialType = 'dye';
        if (purchase.packaging_material) materialType = 'packaging';
        if (purchase.consumable_material) materialType = 'consumable';
        if (purchase.press_form) materialType = 'press_form';
        if ((purchase.equipment_name || '').trim()) materialType = 'equipment';
        setItemRows([{
          tempKey: nextItemKey(),
          materialType,
          raw_material: purchase.raw_material != null && purchase.raw_material !== '' ? String(purchase.raw_material) : '',
          dye: purchase.dye != null && purchase.dye !== '' ? String(purchase.dye) : '',
          packaging_material: purchase.packaging_material != null && purchase.packaging_material !== '' ? String(purchase.packaging_material) : '',
          consumable_material: purchase.consumable_material != null && purchase.consumable_material !== '' ? String(purchase.consumable_material) : '',
          press_form: purchase.press_form != null && purchase.press_form !== '' ? String(purchase.press_form) : '',
          equipment_name: (purchase.equipment_name || '').trim(),
          quantity: purchase.quantity != null ? String(purchase.quantity) : '',
          unit_price: purchase.unit_price != null ? String(purchase.unit_price) : '',
        }]);
      }

      const pays = Array.isArray(purchase.payments) ? purchase.payments : [];
      setPaymentRows(
        pays.map((x) => ({
          tempKey: nextPayKey(),
          id: x.id,
          amount: x.amount != null ? String(x.amount) : '',
          paid_at: toDateTimeLocalFromIso(x.paid_at),
          comment: x.comment || '',
        })),
      );
      const ships = Array.isArray(purchase.shipments) ? purchase.shipments : [];
      const purchaseItems = Array.isArray(purchase.items) ? purchase.items : [];
      // Legacy: одне відвантаження без позиції на всю суму → розбити по позиціях (сировина / барвник окремо)
      const linked = ships.filter((x) => x.purchase_order_item != null && x.purchase_order_item !== '');
      const orphans = ships.filter((x) => x.purchase_order_item == null || x.purchase_order_item === '');
      const itemsQtySum = purchaseItems.reduce((a, it) => a + (Number(it.quantity) || 0), 0);
      const orphanQty = orphans.reduce((a, x) => a + (Number(x.quantity) || 0), 0);
      const canSplitOrphan =
        purchaseItems.length > 1
        && linked.length === 0
        && orphans.length >= 1
        && itemsQtySum > 0
        && Math.abs(orphanQty - itemsQtySum) < 0.011;

      if (canSplitOrphan) {
        const stamp = orphans[0];
        setShipmentRows(
          purchaseItems.map((it) => ({
            tempKey: nextShipKey(),
            // без id — при збереженні старе «сумарне» відвантаження заміниться на окремі
            purchase_order_item: it.id != null ? String(it.id) : '',
            item_temp_key: it.id != null ? `saved-${it.id}` : '',
            quantity: it.quantity != null ? String(it.quantity) : '',
            shipped_at: toDateTimeLocalFromIso(stamp.shipped_at),
            comment: stamp.comment || '',
          })),
        );
      } else {
        setShipmentRows(
          ships.map((x) => {
            const itemId = x.purchase_order_item != null ? String(x.purchase_order_item) : '';
            const matchedItemRow = purchaseItems.find((it) => String(it.id) === itemId);
            return {
              tempKey: nextShipKey(),
              id: x.id,
              purchase_order_item: itemId,
              item_temp_key: matchedItemRow ? `saved-${matchedItemRow.id}` : '',
              quantity: x.quantity != null ? String(x.quantity) : '',
              shipped_at: toDateTimeLocalFromIso(x.shipped_at),
              comment: x.comment || '',
            };
          }),
        );
      }
    } else {
      setPendingFulfillLineId(null);
      setPendingPurchaseRequestId(null);
      setEditing(null);
      setForm({
        purchase_date: new Date().toISOString().slice(0, 10),
        delivery_date_planned: '',
        arrival_date_planned: '',
        arrival_date_actual: '',
        status: 'new',
        supplier: '',
        supplier_name: '',
        comment: '',
      });
      setItemRows([EMPTY_ITEM()]);
      setPaymentRows([]);
      setShipmentRows([]);
    }
    // ЗЕД section
    const fd = purchase?.foreign_detail || null;
    if (fd) {
      setZedEnabled(true);
      setZedDetailId(fd.id);
      setZedForm({
        currency: fd.currency || 'USD',
        exchange_rate: fd.exchange_rate ?? '',
        amount_foreign: fd.amount_foreign ?? '',
        payment_type: fd.payment_type || 'postpayment',
        payment_date: fd.payment_date || '',
        expected_delivery_date: fd.expected_delivery_date || '',
        actual_delivery_date: fd.actual_delivery_date || '',
        logistics_delivery: fd.logistics_delivery ?? '',
        logistics_customs: fd.logistics_customs ?? '',
        logistics_insurance: fd.logistics_insurance ?? '',
        customs_declaration_number: fd.customs_declaration_number || '',
        currency_control_closed: fd.currency_control_closed || false,
        note: fd.note || '',
      });
    } else {
      setZedEnabled(false);
      setZedDetailId(null);
      setZedForm({
        currency: 'USD', exchange_rate: '', amount_foreign: '',
        payment_type: 'postpayment', payment_date: '',
        expected_delivery_date: '', actual_delivery_date: '',
        logistics_delivery: '', logistics_customs: '', logistics_insurance: '',
        customs_declaration_number: '', currency_control_closed: false, note: '',
      });
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setFormError('');
    setPaymentRows([]);
    setShipmentRows([]);
    setItemRows([EMPTY_ITEM()]);
    setPendingFulfillLineId(null);
    setPendingPurchaseRequestId(null);
    setZedEnabled(false);
    setZedDetailId(null);
  };

  const addPaymentRow = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const localNow = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setPaymentRows((prev) => [
      ...prev,
      {
        tempKey: nextPayKey(),
        amount: '',
        paid_at: localNow,
        comment: '',
      },
    ]);
  };

  const removePaymentRow = (key) => {
    setPaymentRows((prev) => prev.filter((r) => r.tempKey !== key));
  };

  const updatePaymentRow = (key, field, value) => {
    setPaymentRows((prev) => prev.map((r) => (r.tempKey === key ? { ...r, [field]: value } : r)));
  };

  const itemRowLabel = (row) => {
    if (!row) return '—';
    if (row.materialType === 'raw') {
      const m = (rawMaterials || []).find((x) => String(x.id) === String(row.raw_material));
      return m ? (m.article ? `${m.article} — ${m.name}` : m.name) : (row.raw_material ? `Сировина #${row.raw_material}` : 'Сировина');
    }
    if (row.materialType === 'dye') {
      const m = (dyes || []).find((x) => String(x.id) === String(row.dye));
      return m ? (m.article ? `${m.article} — ${m.name}` : m.name) : (row.dye ? `Барвник #${row.dye}` : 'Барвник');
    }
    if (row.materialType === 'packaging') {
      const m = (packaging || []).find((x) => String(x.id) === String(row.packaging_material));
      return m ? m.name : 'Упаковка';
    }
    if (row.materialType === 'consumable') {
      const m = (consumables || []).find((x) => String(x.id) === String(row.consumable_material));
      return m ? m.name : 'Розхідний';
    }
    if (row.materialType === 'press_form') {
      const m = (pressForms || []).find((x) => String(x.id) === String(row.press_form));
      if (m) return `${m.identifier} — ${m.name}`;
      return (row.equipment_name || '').trim() || 'Прес-форма';
    }
    if (row.materialType === 'equipment') return (row.equipment_name || '').trim() || 'Обладнання';
    return 'Позиція';
  };

  const itemRowIsComplete = (it) => {
    const hasQty = it.quantity !== '' && Number(it.quantity) > 0;
    if (!hasQty) return false;
    if (it.materialType === 'raw') return !!it.raw_material;
    if (it.materialType === 'dye') return !!it.dye;
    if (it.materialType === 'packaging') return !!it.packaging_material;
    if (it.materialType === 'consumable') return !!it.consumable_material;
    if (it.materialType === 'press_form') return true; // матеріал/довідник не обовʼязковий
    if (it.materialType === 'equipment') return !!(it.equipment_name || '').trim();
    return false;
  };

  const addShipmentRow = (forItemRow = null) => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const localNow = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    const target =
      forItemRow
      || (itemRows.length === 1 ? itemRows[0] : null);
    setShipmentRows((prev) => [
      ...prev,
      {
        tempKey: nextShipKey(),
        purchase_order_item: target?.id != null && target.id !== '' ? String(target.id) : '',
        item_temp_key: target?.tempKey || '',
        // кількість позиції, не сума всіх позицій закупівлі
        quantity: target?.quantity != null && target.quantity !== '' ? String(target.quantity) : '',
        shipped_at: localNow,
        comment: '',
      },
    ]);
  };

  /** Окреме відвантаження на кожну позицію (сировина, барвник, …). */
  const addShipmentsForAllItems = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const localNow = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    const eligible = itemRows.filter((it) => itemRowIsComplete(it));
    if (eligible.length === 0) return;
    setShipmentRows((prev) => {
      const bound = prev.filter((r) => r.item_temp_key || r.purchase_order_item);
      // Якщо був лише «сумарний» рядок без позиції (напр. 10000) — замінюємо, не додаємо зверху
      const base = bound.length > 0 ? bound : [];
      const covered = new Set(
        base.map((r) => r.item_temp_key || `saved-${r.purchase_order_item}`),
      );
      const additions = eligible
        .filter((it) => !covered.has(it.tempKey) && !(it.id != null && covered.has(`saved-${it.id}`)))
        .map((it) => ({
          tempKey: nextShipKey(),
          purchase_order_item: it.id != null && it.id !== '' ? String(it.id) : '',
          item_temp_key: it.tempKey,
          quantity: String(it.quantity),
          shipped_at: localNow,
          comment: '',
        }));
      return additions.length ? [...base, ...additions] : prev;
    });
  };

  const removeShipmentRow = (key) => {
    setShipmentRows((prev) => prev.filter((r) => r.tempKey !== key));
  };

  const updateShipmentRow = (key, field, value) => {
    setShipmentRows((prev) => prev.map((r) => (r.tempKey === key ? { ...r, [field]: value } : r)));
  };

  // Знаходить першого постачальника для матеріалу і підставляє його (якщо поле порожнє)
  const autoFillSupplier = (matType, matId) => {
    if (!matId) return;
    const fieldMap = { raw: 'raw_materials', dye: 'dyes', packaging: 'packaging_materials', consumable: 'consumable_materials' };
    const field = fieldMap[matType];
    if (!field) return;
    const found = (suppliers || []).find((s) => (s[field] || []).map(Number).includes(Number(matId)));
    if (found) {
      setForm((prev) => ({
        ...prev,
        supplier: prev.supplier || found.id,
        supplier_name: prev.supplier ? prev.supplier_name : (found.name || ''),
      }));
    }
  };

  const handleMaterialTypeChange = (value) => {
    setForm((prev) => ({
      ...prev,
      materialType: value,
      raw_material: '',
      dye: '',
      packaging_material: '',
      consumable_material: '',
      press_form: '',
      equipment_name: '',
    }));
  };

  const handleSave = async () => {
    setFormError('');
    if (editing && isAwaitingApproval(editingPurchase)) {
      setFormError(
        'Ця закупівля очікує погодження. Зміни заборонені до статусу «Погоджено» у розділі «Погодження».',
      );
      return;
    }

    // Validate items
    const validItems = itemRows.filter((it) => itemRowIsComplete(it));
    if (validItems.length === 0) {
      setFormError(
        'Додайте хоча б одну позицію з кількістю. Для сировини / барвника / упаковки / розхідних оберіть матеріал; для прес-форми достатньо кількості та ціни.',
      );
      return;
    }
    const incompleteMaterial = itemRows.find((it) => {
      const hasQty = it.quantity !== '' && Number(it.quantity) > 0;
      if (!hasQty) return false;
      if (['raw', 'dye', 'packaging', 'consumable'].includes(it.materialType)) {
        return !itemRowIsComplete(it);
      }
      if (it.materialType === 'equipment') return !(it.equipment_name || '').trim();
      return false;
    });
    if (incompleteMaterial) {
      setFormError('Для обраного типу позиції вкажіть матеріал (або назву обладнання).');
      return;
    }

    const itemsPayload = validItems.map((it) => ({
      ...(it.id != null && it.id !== '' ? { id: Number(it.id) } : {}),
      raw_material: it.materialType === 'raw' ? it.raw_material || null : null,
      dye: it.materialType === 'dye' ? it.dye || null : null,
      packaging_material: it.materialType === 'packaging' ? it.packaging_material || null : null,
      consumable_material: it.materialType === 'consumable' ? it.consumable_material || null : null,
      press_form: it.materialType === 'press_form' ? (it.press_form || null) : null,
      line_kind:
        it.materialType === 'press_form'
          ? 'press_form'
          : (it.materialType === 'equipment' ? 'equipment' : ''),
      equipment_name:
        it.materialType === 'equipment' || it.materialType === 'press_form'
          ? (it.equipment_name || '').trim()
          : '',
      quantity: parseFloat(it.quantity),
      unit_price: it.unit_price !== '' ? parseFloat(it.unit_price) : null,
    }));

    const totalCostCalc = itemsPayload.reduce((sum, it) => {
      const qty = it.quantity || 0;
      const up = it.unit_price != null ? it.unit_price : 0;
      return sum + qty * up;
    }, 0);
    const totalQtyCalc = itemsPayload.reduce((s, it) => s + (it.quantity || 0), 0);

    const payload = {
      items: itemsPayload,
      quantity: Math.round(totalQtyCalc * 10000) / 10000,
      purchase_date: form.purchase_date,
      delivery_date_planned: form.delivery_date_planned || null,
      arrival_date_planned: form.arrival_date_planned || null,
      arrival_date_actual: form.arrival_date_actual || null,
      status: form.status,
      supplier: form.supplier ? Number(form.supplier) : null,
      supplier_name: (form.supplier_name || '').trim(),
      comment: (form.comment || '').trim(),
    };

    const qtyNum = totalQtyCalc;
    const totalCostCalcFinal = Math.round(totalCostCalc * 100) / 100;

    const payIncomplete = paymentRows.some((r) => {
      const hasAmt = r.amount !== '' && r.amount != null && Number(r.amount) > 0;
      const hasDt = Boolean(r.paid_at);
      return (hasAmt && !hasDt) || (hasDt && !hasAmt);
    });
    if (payIncomplete) {
      setFormError('Для кожної оплати вкажіть і суму, і дату');
      return;
    }

    const payments = paymentRows
      .filter((r) => {
        if (r.amount === '' || r.amount == null || Number(r.amount) <= 0 || !r.paid_at) return false;
        return Boolean(paymentPaidAtToIso(r.paid_at));
      })
      .map((r) => ({
        amount: Number(r.amount),
        paid_at: paymentPaidAtToIso(r.paid_at),
        comment: (r.comment || '').trim(),
      }));

    if (totalCostCalcFinal > 0 && payments.length) {
      const sum = payments.reduce((a, p) => a + p.amount, 0);
      if (sum > totalCostCalcFinal + 0.01) {
        setFormError('Сума оплат не може перевищувати вартість закупівлі');
        return;
      }
    }

    const shipIncomplete = shipmentRows.some((r) => {
      const hasQty = r.quantity !== '' && r.quantity != null && Number(r.quantity) > 0;
      const hasDt = Boolean(r.shipped_at);
      return (hasQty && !hasDt) || (hasDt && !hasQty);
    });
    if (shipIncomplete) {
      setFormError('Для кожного відвантаження вкажіть і кількість, і дату');
      return;
    }

    const needItemPick = itemRows.filter((it) => itemRowIsComplete(it)).length > 1;

    const shipMissingItem = shipmentRows.some((r) => {
      const hasQty = r.quantity !== '' && r.quantity != null && Number(r.quantity) > 0;
      if (!hasQty) return false;
      if (!needItemPick) return false;
      return !(r.purchase_order_item || r.item_temp_key);
    });
    if (shipMissingItem) {
      setFormError('Для кожного відвантаження оберіть позицію закупівлі');
      return;
    }

    const shipments = shipmentRows
      .filter((r) => {
        if (r.quantity === '' || r.quantity == null || Number(r.quantity) <= 0 || !r.shipped_at) {
          return false;
        }
        return Boolean(paymentPaidAtToIso(r.shipped_at));
      })
      .map((r) => {
        const row = {
          quantity: Number(r.quantity),
          shipped_at: paymentPaidAtToIso(r.shipped_at),
          comment: (r.comment || '').trim(),
        };
        if (r.id != null && r.id !== '') row.id = Number(r.id);
        if (r.purchase_order_item) {
          row.purchase_order_item = Number(r.purchase_order_item);
        } else if (r.item_temp_key) {
          let idx = itemRows.findIndex((it) => it.tempKey === r.item_temp_key);
          if (idx < 0 && String(r.item_temp_key).startsWith('saved-')) {
            const sid = String(r.item_temp_key).slice(6);
            idx = itemRows.findIndex((it) => String(it.id) === sid);
          }
          if (idx >= 0) {
            if (itemRows[idx].id) row.purchase_order_item = Number(itemRows[idx].id);
            else row.item_index = idx;
          }
        } else if (itemRows.length === 1) {
          if (itemRows[0].id) row.purchase_order_item = Number(itemRows[0].id);
          else row.item_index = 0;
        }
        return row;
      });

    if (qtyNum > 0 && shipments.length) {
      const sumQ = shipments.reduce((a, s) => a + s.quantity, 0);
      if (sumQ > qtyNum + 0.0001) {
        alert('Сума відвантажених кількостей не може перевищувати кількість у закупівлі');
        return;
      }
    }

    // Ліміт по кожній позиції
    const perItemShip = {};
    shipments.forEach((s) => {
      const key = s.purchase_order_item != null
        ? `id:${s.purchase_order_item}`
        : `idx:${s.item_index}`;
      perItemShip[key] = (perItemShip[key] || 0) + s.quantity;
    });
    for (const [key, sq] of Object.entries(perItemShip)) {
      let lim = null;
      let label = '';
      if (key.startsWith('id:')) {
        const it = itemRows.find((r) => String(r.id) === key.slice(3));
        if (it) {
          lim = Number(it.quantity) || 0;
          label = itemRowLabel(it);
        }
      } else if (key.startsWith('idx:')) {
        const idx = Number(key.slice(4));
        const it = itemRows[idx];
        if (it) {
          lim = Number(it.quantity) || 0;
          label = itemRowLabel(it);
        }
      }
      if (lim != null && sq > lim + 0.0001) {
        setFormError(`Відвантаження по «${label}» (${sq}) перевищує кількість позиції (${lim})`);
        return;
      }
    }

    payload.payments = payments;
    payload.shipments = shipments;

    if (!editing && pendingFulfillLineId != null) {
      payload.fulfill_request_line_id = pendingFulfillLineId;
    }
    if (!editing && pendingPurchaseRequestId != null) {
      payload.purchase_request = pendingPurchaseRequestId;
    }

    if (!payload.purchase_date) {
      setFormError('Вкажіть дату закупівлі');
      return;
    }

    try {
      let savedId = editing;
      if (editing) {
        await purchaseOrderService.update(editing, payload);
      } else {
        const res = await purchaseOrderService.create(payload);
        savedId = res.data?.id;
        notifyPurchaseApprovalsChanged();
      }

      // Save / update / delete ForeignPurchaseDetail
      if (savedId) {
        const api = (await import('../services/api')).default;
        if (zedEnabled) {
          const zedPayload = {
            purchase: savedId,
            currency: zedForm.currency,
            exchange_rate: zedForm.exchange_rate ? Number(zedForm.exchange_rate) : null,
            amount_foreign: zedForm.amount_foreign ? Number(zedForm.amount_foreign) : null,
            payment_type: zedForm.payment_type,
            payment_date: zedForm.payment_date || null,
            expected_delivery_date: zedForm.expected_delivery_date || null,
            actual_delivery_date: zedForm.actual_delivery_date || null,
            logistics_delivery: zedForm.logistics_delivery ? Number(zedForm.logistics_delivery) : 0,
            logistics_customs: zedForm.logistics_customs ? Number(zedForm.logistics_customs) : 0,
            logistics_insurance: zedForm.logistics_insurance ? Number(zedForm.logistics_insurance) : 0,
            customs_declaration_number: zedForm.customs_declaration_number || '',
            currency_control_closed: zedForm.currency_control_closed,
            note: zedForm.note || '',
          };
          if (zedDetailId) {
            await api.put(`/foreign-purchase-details/${zedDetailId}/`, zedPayload);
          } else {
            await api.post('/foreign-purchase-details/', zedPayload);
          }
        } else if (zedDetailId) {
          await api.delete(`/foreign-purchase-details/${zedDetailId}/`);
        }
      }

      closeDialog();
      loadData();
      if (tabValue === REQUESTS_TAB_INDEX) loadPurchaseRequests();
    } catch (error) {
      console.error(error);
      const msg =
        error.response?.data &&
        (typeof error.response.data === 'string'
          ? error.response.data
          : JSON.stringify(error.response.data, null, 2));
      setFormError(`Помилка збереження: ${msg || error.message || 'Невідома помилка'}`);
    }
  };

  const handleStatusQuickChange = async (purchase, newStatus) => {
    if (isAwaitingApproval(purchase)) {
      alert('Закупівля ще не погоджена — зміну статусу заборонено.');
      return;
    }
    if (isRejectedApproval(purchase)) {
      alert(
        'Закупівлю відхилено: змінювати робочий статус заборонено до повторного погодження (кнопка в картці закупівлі).',
      );
      return;
    }
    try {
      await purchaseOrderService.patch(purchase.id, { status: newStatus });
      loadData();
    } catch (error) {
      console.error(error);
      alert('Не вдалося змінити статус');
    }
  };

  const handleRequestReapproval = async () => {
    if (!editing || !isRejectedApproval(editingPurchase)) return;
    if (!canRequestPurchaseReapproval) {
      alert('Для вас заборонено розділ «Закупівлі» у налаштуваннях ролі — повторне погодження недоступне.');
      return;
    }
    if (!window.confirm('Надіслати закупівлю на повторне погодження? Коментар відхилення буде очищено.')) {
      return;
    }
    setReapprovalSubmitting(true);
    try {
      await purchaseOrderService.requestReapproval(editing);
      notifyPurchaseApprovalsChanged();
      closeDialog();
      loadData();
      if (tabValue === REQUESTS_TAB_INDEX) {
        loadPurchaseRequests();
      }
    } catch (error) {
      console.error(error);
      const msg =
        error.response?.data &&
        (typeof error.response.data === 'string'
          ? error.response.data
          : JSON.stringify(error.response.data, null, 2));
      alert(`Не вдалося надіслати на погодження:\n${msg || error.message || 'Невідома помилка'}`);
    } finally {
      setReapprovalSubmitting(false);
    }
  };

  const renderMaterialName = (p) => {
    // Multi-item orders
    const items = Array.isArray(p.items) ? p.items : [];
    if (items.length > 0) {
      const names = items.map((it) => (
        it.raw_material_name || it.dye_name || it.packaging_material_name ||
        it.consumable_material_name || it.press_form_identifier ||
        (it.equipment_name || '').trim() || '?'
      ));
      if (names.length === 1) return names[0];
      return `${names[0]} +${names.length - 1}`;
    }
    // Legacy single-item
    const eq = (p.equipment_name || '').trim();
    if (eq) return eq;
    if (p.raw_material_name) return p.raw_material_name;
    if (p.dye_name) return p.dye_name;
    if (p.packaging_material_name) return p.packaging_material_name;
    if (p.consumable_material_name) return p.consumable_material_name;
    if (p.press_form_identifier || p.press_form_name) return `${p.press_form_identifier || ''} — ${p.press_form_name || ''}`.trim().replace(/^— |— $/, '');
    return '—';
  };

  // Ціна в багатопозиційних закупівлях зберігається в позиціях (у шапці — null)
  const renderUnitPrice = (p) => {
    if (p.unit_price != null) return p.unit_price;
    const prices = (Array.isArray(p.items) ? p.items : [])
      .map((it) => it.unit_price)
      .filter((v) => v != null);
    if (!prices.length) return '—';
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return min === max ? min : `${min}–${max}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PurchasesNav currentUser={currentUser} />

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Закупівлі</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            if (tabValue === REQUESTS_TAB_INDEX) setTabValue(0);
            openDialog(null);
          }}
        >
          Додати закупівлю
        </Button>
      </Box>

      <Tabs value={tabValue} onChange={handleMainTabChange} sx={{ mb: 2 }}>
        <Tab label="Сировина" />
        <Tab label="Барвники" />
        <Tab label="Упаковка" />
        <Tab label="Розхідні" />
        <Tab label="Прес-форми" />
        <Tab label="Обладнання" />
        <Tab label="Заявки" />
        <Tab label="ЗЕД" />
      </Tabs>

      {tabValue === ZED_TAB_INDEX ? (() => {
        const zedPurchases = purchases.filter((p) => p.foreign_detail);
        const hasOverdue = zedPurchases.some((p) => (p.foreign_detail?.days_overdue || 0) > 0);
        const payTypeLabel = { prepayment: 'Передоплата', postpayment: 'Постоплата', partial: 'Часткова' };
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Іноземні закупівлі — позначте "Іноземна закупівля (ЗЕД)" при редагуванні будь-якої закупівлі. Вона з'явиться тут.
            </Alert>
            {hasOverdue && (
              <Alert severity="error" sx={{ mb: 2 }} icon={<WarningAmberIcon />}>
                Є прострочені валютні контракти! Перегляньте рядки з червоним тлом.
              </Alert>
            )}
            {loading ? <CircularProgress /> : (
              <TableContainer component={Paper}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Матеріал</TableCell>
                      <TableCell>Постачальник</TableCell>
                      <TableCell>Валюта</TableCell>
                      <TableCell align="right">Сума (вал.)</TableCell>
                      <TableCell align="right">Сума (грн)</TableCell>
                      <TableCell>Тип оплати</TableCell>
                      <TableCell>Дата оплати</TableCell>
                      <TableCell>Дедлайн ВК</TableCell>
                      <TableCell align="right">Прострочення</TableCell>
                      <TableCell align="right">Штраф, грн</TableCell>
                      <TableCell align="right">Логістика, грн</TableCell>
                      <TableCell>Статус ВК</TableCell>
                      <TableCell align="right">Митна декл.</TableCell>
                      <TableCell align="center">Дії</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {zedPurchases.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={14} align="center" sx={{ color: 'text.secondary' }}>
                          Немає іноземних закупівель. Відредагуйте закупівлю і увімкніть секцію "ЗЕД".
                        </TableCell>
                      </TableRow>
                    ) : (
                      zedPurchases.map((p) => {
                        const fd = p.foreign_detail;
                        const overdue = fd?.days_overdue || 0;
                        return (
                          <TableRow key={p.id} hover sx={overdue > 0 ? { bgcolor: 'error.light' } : {}}>
                            <TableCell>{p.raw_material_name || p.dye_name || p.packaging_material_name || p.consumable_material_name || p.equipment_name || '—'}</TableCell>
                            <TableCell>{(p.supplier_name || '').trim() || '—'}</TableCell>
                            <TableCell>{fd?.currency || '—'}</TableCell>
                            <TableCell align="right">{fd?.amount_foreign ? Number(fd.amount_foreign).toLocaleString('uk-UA') : '—'}</TableCell>
                            <TableCell align="right">{fd?.amount_hryvnia ? Number(fd.amount_hryvnia).toLocaleString('uk-UA', { maximumFractionDigits: 2 }) : '—'}</TableCell>
                            <TableCell>{payTypeLabel[fd?.payment_type] || fd?.payment_type || '—'}</TableCell>
                            <TableCell>{fd?.payment_date || '—'}</TableCell>
                            <TableCell>{fd?.currency_control_deadline || '—'}</TableCell>
                            <TableCell align="right">
                              {overdue > 0 ? <Chip size="small" color="error" label={`${overdue} дн`} /> : '0'}
                            </TableCell>
                            <TableCell align="right">
                              {fd?.penalty_amount > 0 ? <Chip size="small" color="error" label={Number(fd.penalty_amount).toLocaleString('uk-UA', { maximumFractionDigits: 2 })} /> : '0'}
                            </TableCell>
                            <TableCell align="right">{fd?.total_logistics ? Number(fd.total_logistics).toLocaleString('uk-UA', { maximumFractionDigits: 2 }) : '0'}</TableCell>
                            <TableCell>
                              <Chip size="small" color={fd?.currency_control_closed ? 'success' : 'warning'}
                                label={fd?.currency_control_closed ? 'Закрито' : 'Відкрито'} />
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 11 }}>{fd?.customs_declaration_number || '—'}</TableCell>
                            <TableCell align="center">
                              <Button size="small" startIcon={<EditIcon />} onClick={() => openDialog(p)}>
                                Редагувати
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        );
      })() : tabValue === REQUESTS_TAB_INDEX ? (
        <Paper sx={{ p: 0 }}>
          {purchaseRequestsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <TableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Заявка</TableCell>
                    <TableCell>Замовлення клієнта</TableCell>
                    <TableCell>Матеріал</TableCell>
                    <TableCell align="right">Потрібно добрати</TableCell>
                    <TableCell>Одиниця</TableCell>
                    <TableCell>Закупівля</TableCell>
                    <TableCell align="right">Дії</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchaseRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="textSecondary">Немає заявок</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    purchaseRequests.flatMap((req) =>
                      Array.isArray(req.lines) && req.lines.length
                        ? req.lines.map((ln, lineIdx) => (
                            <TableRow
                              key={`${req.id}-${ln.id}`}
                              id={lineIdx === 0 ? `pr-row-${req.id}` : undefined}
                              sx={
                                highlightPrId === req.id
                                  ? (theme) => ({ bgcolor: theme.palette.action.selected })
                                  : undefined
                              }
                            >
                              <TableCell>#{req.id}</TableCell>
                              <TableCell>{req.customer_order_number || '—'}</TableCell>
                              <TableCell>{ln.material_name}</TableCell>
                              <TableCell align="right">{ln.quantity_needed}</TableCell>
                              <TableCell>
                                {ln.unit
                                  || (ln.packaging_material || ln.packaging_material_id ? 'шт' : 'кг')}
                              </TableCell>
                              <TableCell>
                                {ln.fulfilled_by_purchase ? (
                                  <Chip size="small" label={`№${ln.fulfilled_by_purchase}`} color="success" />
                                ) : (
                                  '—'
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={Boolean(ln.fulfilled_by_purchase) || req.status !== 'open'}
                                  onClick={() => openPurchaseFromRequestLine(req, ln)}
                                >
                                  Створити закупівлю
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        : [
                            <TableRow
                              key={`${req.id}-empty`}
                              id={`pr-row-${req.id}`}
                              sx={
                                highlightPrId === req.id
                                  ? (theme) => ({ bgcolor: theme.palette.action.selected })
                                  : undefined
                              }
                            >
                              <TableCell>#{req.id}</TableCell>
                              <TableCell colSpan={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Немає рядків
                                </Typography>
                              </TableCell>
                            </TableRow>,
                          ],
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      ) : (
      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Матеріал</TableCell>
              <TableCell>Постачальник</TableCell>
              <TableCell>Коментар</TableCell>
              <TableCell>Кількість</TableCell>
              <TableCell align="right">Відвант. к-ть</TableCell>
              <TableCell align="right">Залишок к-ть</TableCell>
              <TableCell>Ціна</TableCell>
              <TableCell>Вартість</TableCell>
              <TableCell align="right">Оплачено</TableCell>
              <TableCell align="right">Залишок</TableCell>
              <TableCell>Склад</TableCell>
              <TableCell>Дата закупівлі</TableCell>
              <TableCell>План відгрузки</TableCell>
              <TableCell>Планова дата прибуття</TableCell>
              <TableCell>Фактична дата прибуття</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Погодження</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPurchases.length === 0 ? (
              <TableRow>
                <TableCell colSpan={17} align="center">
                  <Typography color="textSecondary">Немає закупівель</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredPurchases.map((p) => (
                <TableRow
                  key={p.id}
                  id={`po-row-${p.id}`}
                  sx={
                    highlightPoId === p.id
                      ? (theme) => ({ bgcolor: theme.palette.action.selected })
                      : undefined
                  }
                >
                  <TableCell>{renderMaterialName(p)}</TableCell>
                  <TableCell>{(p.supplier_name || '').trim() || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    {(p.comment || '').trim() ? (
                      <Tooltip title={(p.comment || '').trim()}>
                        <Typography variant="body2" noWrap>
                          {(p.comment || '').trim()}
                        </Typography>
                      </Tooltip>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{p.quantity}</TableCell>
                  <TableCell align="right">
                    {p.shipped_total != null && p.shipped_total !== undefined ? p.shipped_total : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {p.remaining_quantity != null && p.remaining_quantity !== undefined
                      ? p.remaining_quantity
                      : '—'}
                  </TableCell>
                  <TableCell>{renderUnitPrice(p)}</TableCell>
                  <TableCell>{p.total_cost ?? '—'}</TableCell>
                  <TableCell align="right">
                    {p.paid_total != null && p.paid_total !== undefined ? p.paid_total : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {p.remaining_amount != null && p.remaining_amount !== undefined
                      ? p.remaining_amount
                      : '—'}
                  </TableCell>
                  <TableCell>{getWarehouseTitleForPurchase(p)}</TableCell>
                  <TableCell>
                    {p.purchase_date ? new Date(p.purchase_date).toLocaleDateString('uk-UA') : '—'}
                  </TableCell>
                  <TableCell>
                    {p.delivery_date_planned
                      ? new Date(p.delivery_date_planned).toLocaleDateString('uk-UA')
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {p.arrival_date_planned
                      ? new Date(p.arrival_date_planned).toLocaleDateString('uk-UA')
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {p.arrival_date_actual
                      ? new Date(p.arrival_date_actual).toLocaleDateString('uk-UA')
                      : '—'}
                  </TableCell>
                  <TableCell>{p.status_display || p.status}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={p.approval_status_display || p.approval_status || '—'}
                      color={
                        p.approval_status === 'approved'
                          ? 'success'
                          : p.approval_status === 'rejected'
                          ? 'error'
                          : 'warning'
                      }
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      onClick={() => openDialog(p)}
                      startIcon={<EditIcon />}
                      sx={{ mr: 1 }}
                    >
                      {isAwaitingApproval(p) ? 'Переглянути' : 'Редагувати'}
                    </Button>
                    {p.status !== 'completed' && (
                      <TextField
                        select
                        size="small"
                        value=""
                        onChange={(e) => handleStatusQuickChange(p, e.target.value)}
                        sx={{ width: 140 }}
                        label="Змінити статус"
                        disabled={isAwaitingApproval(p) || isRejectedApproval(p)}
                      >
                        {STATUS_OPTIONS.filter((s) => s.value !== p.status).map((s) => (
                          <MenuItem key={s.value} value={s.value}>
                            {s.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      <Dialog
        key={
          pendingFulfillLineId != null
            ? `from-pr-${pendingPurchaseRequestId}-${pendingFulfillLineId}`
            : editing
              ? `edit-${editing}`
              : 'new-purchase'
        }
        open={dialogOpen}
        onClose={closeDialog}
        fullScreen
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pr: 1 }}>
          <span>
            {editing
              ? 'Редагувати закупівлю'
              : pendingPurchaseRequestId != null
                ? `Нова закупівля (з заявки #${pendingPurchaseRequestId})`
                : 'Нова закупівля'}
          </span>
          <IconButton edge="end" onClick={closeDialog} aria-label="Закрити" size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {formError ? (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }} onClose={() => setFormError('')}>
              {formError}
            </Alert>
          ) : null}
          {dialogReadOnly ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Закупівля очікує погодження. Редагування та збереження заборонені до статусу «Погоджено» у розділі
              «Погодження».
            </Alert>
          ) : null}
          {editingPurchase && isRejectedApproval(editingPurchase) ? (
            <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Закупівлю відхилено під час погодження
              </Typography>
              {(editingPurchase.approval_comment || '').trim() ? (
                <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                  Коментар з погодження: {(editingPurchase.approval_comment || '').trim()}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Коментар з погодження не залишено.
                </Typography>
              )}
              <Typography variant="body2" sx={{ mt: 1 }}>
                Робочий статус закупівлі (Нова / В роботі / Замовлено / Виконано) змінювати не можна, доки закупівлю
                знову не погодять. Можна виправити дані та натиснути «Повторне погодження».
              </Typography>
            </Alert>
          ) : null}
          <Box mt={1} sx={dialogReadOnly ? { pointerEvents: 'none', opacity: 0.88 } : undefined}>
            <Autocomplete
              options={supplierOptions}
              isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.label || '')}
              value={supplierOptions.find((s) => Number(s.id) === Number(form.supplier)) || null}
              onChange={(_, opt) => {
                if (!opt) {
                  setForm((prev) => ({ ...prev, supplier: '', supplier_name: prev.supplier_name }));
                  return;
                }
                setForm((prev) => ({
                  ...prev,
                  supplier: opt.id,
                  supplier_name: opt.name || prev.supplier_name,
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Постачальник (довідник)"
                  margin="normal"
                  placeholder="Оберіть або залиште порожнім"
                />
              )}
            />
            <TextField
              label="Назва постачальника (у документах)"
              fullWidth
              margin="normal"
              value={form.supplier_name}
              onChange={(e) => setForm((prev) => ({ ...prev, supplier_name: e.target.value }))}
              helperText="Заповнюється з довідника або вручну, якщо постачальника немає у списку"
            />
            {/* ── Позиції закупівлі ────────────────────────────────────── */}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Позиції закупівлі
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 140 }}>Тип</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>Матеріал / назва</TableCell>
                    <TableCell sx={{ minWidth: 110 }}>Кількість</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>Ціна / од.</TableCell>
                    <TableCell sx={{ minWidth: 110 }}>Сума</TableCell>
                    <TableCell width={48} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itemRows.map((row) => {
                    const rowTotal = (() => {
                      const q = parseFloat(row.quantity);
                      const u = parseFloat(row.unit_price);
                      if (Number.isFinite(q) && Number.isFinite(u)) return Math.round(q * u * 100) / 100;
                      return null;
                    })();
                    const updateItem = (field, value) =>
                      setItemRows((prev) => prev.map((r) => r.tempKey === row.tempKey ? { ...r, [field]: value } : r));
                    const updateItemType = (value) =>
                      setItemRows((prev) => prev.map((r) => r.tempKey === row.tempKey ? {
                        ...r, materialType: value,
                        raw_material: '', dye: '', packaging_material: '',
                        consumable_material: '', press_form: '', equipment_name: '',
                      } : r));
                    return (
                      <TableRow key={row.tempKey}>
                        <TableCell>
                          <TextField
                            select size="small" fullWidth
                            value={row.materialType}
                            onChange={(e) => updateItemType(e.target.value)}
                          >
                            <MenuItem value="raw">Сировина</MenuItem>
                            <MenuItem value="dye">Барвник</MenuItem>
                            <MenuItem value="packaging">Упаковка</MenuItem>
                            <MenuItem value="consumable">Розхідний</MenuItem>
                            <MenuItem value="press_form">Прес-форма</MenuItem>
                            <MenuItem value="equipment">Обладнання</MenuItem>
                          </TextField>
                        </TableCell>
                        <TableCell>
                          {row.materialType === 'raw' && (
                            <TextField select size="small" fullWidth value={row.raw_material === '' || row.raw_material == null ? '' : String(row.raw_material)}
                              onChange={(e) => updateItem('raw_material', e.target.value)}>
                              <MenuItem value=""><em>— оберіть —</em></MenuItem>
                              {rawMaterials.map((m) => (
                                <MenuItem key={m.id} value={String(m.id)}>{m.article ? `${m.article} — ${m.name}` : m.name}</MenuItem>
                              ))}
                            </TextField>
                          )}
                          {row.materialType === 'dye' && (
                            <TextField select size="small" fullWidth value={row.dye === '' || row.dye == null ? '' : String(row.dye)}
                              onChange={(e) => updateItem('dye', e.target.value)}>
                              <MenuItem value=""><em>— оберіть —</em></MenuItem>
                              {dyes.map((m) => (
                                <MenuItem key={m.id} value={String(m.id)}>{m.article ? `${m.article} — ${m.name}` : m.name}</MenuItem>
                              ))}
                            </TextField>
                          )}
                          {row.materialType === 'packaging' && (
                            <TextField select size="small" fullWidth value={row.packaging_material === '' || row.packaging_material == null ? '' : String(row.packaging_material)}
                              onChange={(e) => updateItem('packaging_material', e.target.value)}>
                              <MenuItem value=""><em>— оберіть —</em></MenuItem>
                              {packaging.map((m) => (
                                <MenuItem key={m.id} value={String(m.id)}>{m.article ? `${m.article} — ${m.name}` : m.name}</MenuItem>
                              ))}
                            </TextField>
                          )}
                          {row.materialType === 'consumable' && (
                            <TextField select size="small" fullWidth value={row.consumable_material === '' || row.consumable_material == null ? '' : String(row.consumable_material)}
                              onChange={(e) => updateItem('consumable_material', e.target.value)}>
                              <MenuItem value=""><em>— оберіть —</em></MenuItem>
                              {consumables.map((m) => (
                                <MenuItem key={m.id} value={String(m.id)}>{m.article ? `${m.article} — ${m.name}` : m.name}</MenuItem>
                              ))}
                            </TextField>
                          )}
                          {row.materialType === 'press_form' && (
                            <TextField
                              size="small"
                              fullWidth
                              value={row.equipment_name}
                              onChange={(e) => updateItem('equipment_name', e.target.value)}
                              placeholder="Назва / опис (необовʼязково)"
                              helperText="Достатньо кількості та ціни"
                            />
                          )}
                          {row.materialType === 'equipment' && (
                            <TextField size="small" fullWidth value={row.equipment_name}
                              onChange={(e) => updateItem('equipment_name', e.target.value)}
                              placeholder="Назва обладнання" />
                          )}
                        </TableCell>
                        <TableCell>
                          <TextField size="small" type="number" value={row.quantity}
                            onChange={(e) => updateItem('quantity', e.target.value)}
                            inputProps={{ min: 0.001, step: 'any' }} sx={{ width: 100 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" type="number" value={row.unit_price}
                            onChange={(e) => updateItem('unit_price', e.target.value)}
                            inputProps={{ min: 0, step: 'any' }} sx={{ width: 110 }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {rowTotal != null ? rowTotal.toLocaleString('uk-UA', { maximumFractionDigits: 2 }) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error"
                            disabled={itemRows.length === 1}
                            onClick={() => setItemRows((prev) => prev.filter((r) => r.tempKey !== row.tempKey))}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Button size="small" startIcon={<AddIcon />}
                onClick={() => setItemRows((prev) => [...prev, EMPTY_ITEM()])}>
                Додати позицію
              </Button>
              {itemsTotal > 0 && (
                <Typography variant="body2" fontWeight={600}>
                  Загальна сума: {Math.round(itemsTotal * 100) / 100} грн
                  {paymentsSumInForm > 0 && ` · Оплачено: ${paymentsSumInForm} · Залишок: ${Math.max(0, Math.round((itemsTotal - paymentsSumInForm) * 100) / 100)}`}
                </Typography>
              )}
            </Box>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Часткові оплати
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Сума всіх оплат не може перевищувати вартість (кількість × ціна). Можна додати кілька платежів.
            </Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addPaymentRow} sx={{ mb: 1 }}>
              Додати оплату
            </Button>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Сума</TableCell>
                    <TableCell>Дата оплати</TableCell>
                    <TableCell>Коментар</TableCell>
                    <TableCell width={48} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography color="text.secondary" variant="body2">
                          Немає записів оплат
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paymentRows.map((row) => (
                      <TableRow key={row.tempKey}>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={row.amount}
                            onChange={(e) => updatePaymentRow(row.tempKey, 'amount', e.target.value)}
                            inputProps={{ min: 0.01, step: 'any' }}
                            placeholder="0"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="datetime-local"
                            value={row.paid_at}
                            onChange={(e) => updatePaymentRow(row.tempKey, 'paid_at', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={row.comment}
                            onChange={(e) => updatePaymentRow(row.tempKey, 'comment', e.target.value)}
                            placeholder="Необовʼязково"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => removePaymentRow(row.tempKey)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Часткові відвантаження
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Кожна партія привʼязана до конкретної позиції. Після збереження (і погодження) матеріал
              зʼявиться у вкладці «Надходження» відповідного складу — залишок поповниться лише після
              підтвердження комірником.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {itemRows.length > 1 && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={addShipmentsForAllItems}
                  sx={{ textTransform: 'none', bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                >
                  Окремо по кожній позиції
                </Button>
              )}
              {itemRows.map((it) => (
                <Button
                  key={it.tempKey}
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => addShipmentRow(it)}
                  disabled={!itemRowIsComplete(it)}
                >
                  Відвантаження: {itemRowLabel(it)}
                </Button>
              ))}
              {itemRows.length === 0 && (
                <Button size="small" startIcon={<AddIcon />} onClick={() => addShipmentRow()} sx={{ mb: 0 }}>
                  Додати відвантаження
                </Button>
              )}
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 200 }}>Позиція</TableCell>
                    <TableCell>Кількість</TableCell>
                    <TableCell>Дата відвантаження</TableCell>
                    <TableCell>Коментар</TableCell>
                    <TableCell width={48} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shipmentRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography color="text.secondary" variant="body2">
                          Немає записів відвантажень
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    shipmentRows.map((row) => (
                      <TableRow key={row.tempKey}>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            fullWidth
                            value={
                              row.item_temp_key
                              || (row.purchase_order_item ? `saved-${row.purchase_order_item}` : '')
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              const it = itemRows.find(
                                (r) => r.tempKey === val || `saved-${r.id}` === val,
                              );
                              setShipmentRows((prev) =>
                                prev.map((r) =>
                                  r.tempKey === row.tempKey
                                    ? {
                                      ...r,
                                      item_temp_key: it?.tempKey || val,
                                      purchase_order_item: it?.id != null && it.id !== '' ? String(it.id) : '',
                                    }
                                    : r,
                                ),
                              );
                            }}
                          >
                            <MenuItem value=""><em>— оберіть позицію —</em></MenuItem>
                            {itemRows.map((it) => (
                              <MenuItem key={it.tempKey} value={it.tempKey}>
                                {itemRowLabel(it)}
                                {it.quantity ? ` (план ${it.quantity})` : ''}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={row.quantity}
                            onChange={(e) =>
                              updateShipmentRow(row.tempKey, 'quantity', e.target.value)
                            }
                            inputProps={{ min: 0.01, step: 'any' }}
                            placeholder="0"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="datetime-local"
                            value={row.shipped_at}
                            onChange={(e) =>
                              updateShipmentRow(row.tempKey, 'shipped_at', e.target.value)
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={row.comment}
                            onChange={(e) =>
                              updateShipmentRow(row.tempKey, 'comment', e.target.value)
                            }
                            placeholder="Необовʼязково"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeShipmentRow(row.tempKey)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TextField
              label="Дата закупівлі"
              type="date"
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              value={form.purchase_date}
              onChange={(e) => setForm((prev) => ({ ...prev, purchase_date: e.target.value }))}
            />
            <TextField
              label="Дата відгрузки нам (план)"
              type="date"
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              value={form.delivery_date_planned}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, delivery_date_planned: e.target.value }))
              }
            />
            <TextField
              label="Планова дата прибуття"
              type="date"
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              value={form.arrival_date_planned}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, arrival_date_planned: e.target.value }))
              }
            />

            <TextField
              label="Фактична дата прибуття"
              type="date"
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              value={form.arrival_date_actual}
              onChange={(e) => setForm((prev) => ({ ...prev, arrival_date_actual: e.target.value }))}
              disabled={form.status !== 'completed'}
            />
            <TextField
              label="Коментар"
              fullWidth
              margin="normal"
              multiline
              minRows={2}
              value={form.comment}
              onChange={(e) => setForm((prev) => ({ ...prev, comment: e.target.value }))}
              placeholder="Довільні примітки до закупівлі"
            />
            <TextField
              select
              label="Статус"
              fullWidth
              margin="normal"
              value={form.status}
              onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
              disabled={Boolean(editingPurchase && isRejectedApproval(editingPurchase))}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* ─── ЗЕД секція ─────────────────────────────────────────────── */}
          <Divider sx={{ my: 2 }} />
          <Accordion
            expanded={zedEnabled}
            onChange={(_, expanded) => setZedEnabled(expanded)}
            disableGutters
            sx={{ boxShadow: 'none', border: '1px solid', borderColor: zedEnabled ? 'primary.main' : 'divider', borderRadius: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PublicIcon color={zedEnabled ? 'primary' : 'disabled'} fontSize="small" />
                <Typography fontWeight={600}>Іноземна закупівля (ЗЕД)</Typography>
                {zedEnabled && zedForm.payment_date && (() => {
                  const deadline = new Date(zedForm.payment_date);
                  deadline.setDate(deadline.getDate() + 180);
                  const daysLeft = Math.ceil((deadline - Date.now()) / 86400000);
                  if (daysLeft < 0) return (
                    <Chip size="small" color="error" icon={<WarningAmberIcon />}
                      label={`Прострочено ${Math.abs(daysLeft)} дн`} />
                  );
                  if (daysLeft <= 30) return (
                    <Chip size="small" color="warning" label={`ВК: ${daysLeft} дн`} />
                  );
                  return null;
                })()}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  select fullWidth label="Валюта" size="small"
                  value={zedForm.currency}
                  onChange={(e) => setZedForm((f) => ({ ...f, currency: e.target.value }))}
                >
                  {['USD', 'EUR', 'GBP', 'CNY', 'PLN'].map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  type="number" label="Курс НБУ (грн)" size="small" fullWidth
                  value={zedForm.exchange_rate}
                  onChange={(e) => setZedForm((f) => ({ ...f, exchange_rate: e.target.value }))}
                  inputProps={{ min: 0, step: 0.0001 }}
                />
                <TextField
                  type="number" label="Сума у валюті" size="small" fullWidth
                  value={zedForm.amount_foreign}
                  onChange={(e) => setZedForm((f) => ({ ...f, amount_foreign: e.target.value }))}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText={
                    zedForm.amount_foreign && zedForm.exchange_rate
                      ? `≈ ${(Number(zedForm.amount_foreign) * Number(zedForm.exchange_rate)).toLocaleString('uk-UA', { maximumFractionDigits: 2 })} грн`
                      : ''
                  }
                />
                <TextField
                  select fullWidth label="Тип оплати" size="small"
                  value={zedForm.payment_type}
                  onChange={(e) => setZedForm((f) => ({ ...f, payment_type: e.target.value }))}
                >
                  <MenuItem value="prepayment">Передоплата</MenuItem>
                  <MenuItem value="postpayment">Постоплата</MenuItem>
                  <MenuItem value="partial">Часткова</MenuItem>
                </TextField>
                <TextField
                  type="date" label="Дата оплати" size="small" fullWidth
                  value={zedForm.payment_date}
                  onChange={(e) => setZedForm((f) => ({ ...f, payment_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  helperText={zedForm.payment_date ? `Дедлайн ВК: ${(() => { const d = new Date(zedForm.payment_date); d.setDate(d.getDate()+180); return d.toLocaleDateString('uk-UA'); })()}` : ''}
                />
                <TextField
                  type="date" label="Очікувана дата постачання" size="small" fullWidth
                  value={zedForm.expected_delivery_date}
                  onChange={(e) => setZedForm((f) => ({ ...f, expected_delivery_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  type="date" label="Фактична дата постачання" size="small" fullWidth
                  value={zedForm.actual_delivery_date}
                  onChange={(e) => setZedForm((f) => ({ ...f, actual_delivery_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Митна декларація №" size="small" fullWidth
                  value={zedForm.customs_declaration_number}
                  onChange={(e) => setZedForm((f) => ({ ...f, customs_declaration_number: e.target.value }))}
                />
                <TextField
                  type="number" label="Логістика доставки, грн" size="small" fullWidth
                  value={zedForm.logistics_delivery}
                  onChange={(e) => setZedForm((f) => ({ ...f, logistics_delivery: e.target.value }))}
                  inputProps={{ min: 0, step: 0.01 }}
                />
                <TextField
                  type="number" label="Митне оформлення, грн" size="small" fullWidth
                  value={zedForm.logistics_customs}
                  onChange={(e) => setZedForm((f) => ({ ...f, logistics_customs: e.target.value }))}
                  inputProps={{ min: 0, step: 0.01 }}
                />
                <TextField
                  type="number" label="Страхування, грн" size="small" fullWidth
                  value={zedForm.logistics_insurance}
                  onChange={(e) => setZedForm((f) => ({ ...f, logistics_insurance: e.target.value }))}
                  inputProps={{ min: 0, step: 0.01 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={zedForm.currency_control_closed}
                      onChange={(e) => setZedForm((f) => ({ ...f, currency_control_closed: e.target.checked }))}
                    />
                  }
                  label="Валютний контроль закрито"
                />
                <TextField
                  label="Примітка" size="small" fullWidth multiline rows={2}
                  value={zedForm.note}
                  onChange={(e) => setZedForm((f) => ({ ...f, note: e.target.value }))}
                  sx={{ gridColumn: 'span 2' }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </DialogContent>
        <DialogActions>
          {editingPurchase && isRejectedApproval(editingPurchase) && canRequestPurchaseReapproval ? (
            <Button
              color="primary"
              variant="outlined"
              onClick={handleRequestReapproval}
              disabled={reapprovalSubmitting || dialogReadOnly}
              sx={{ mr: 'auto' }}
            >
              Повторне погодження
            </Button>
          ) : null}
          <Button onClick={closeDialog}>Скасувати</Button>
          <Button variant="contained" onClick={handleSave} disabled={dialogReadOnly}>
            Зберегти
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PurchasesPage;


